import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import { useChat } from 'hooks/useChat';
import { Camera } from 'lucide-react';
import ProfessionalDashboardNavbar from 'pages/professional-dashboard/components/ProfessionalDashboardNavbar';
import Footer from 'pages/landing-page/components/Footer';

const ProfessionalPaginaPubblica = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Data state
  const [fullName, setFullName] = useState('');
  const [professionText, setProfessionText] = useState('');
  const [workAreas, setWorkAreas] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);

  // Form state
  const [rawInput, setRawInput] = useState('');
  const [generatedBio, setGeneratedBio] = useState('');
  const [generatedTagline, setGeneratedTagline] = useState('');
  const [generatedSeoTitle, setGeneratedSeoTitle] = useState('');
  const [generatedSeoDescription, setGeneratedSeoDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [existingSlug, setExistingSlug] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');
  const [generateError, setGenerateError] = useState('');
  const [dataLoading, setDataLoading] = useState(true);

  // Track if this is an existing profile (for manual_edit_count increment)
  const [isExistingProfile, setIsExistingProfile] = useState(false);
  const [existingManualEditCount, setExistingManualEditCount] = useState(0);

  // Stats state
  const [viewCount, setViewCount] = useState(0);
  const [leadCount, setLeadCount] = useState(0);

  // Gallery state — array of {url: string, caption: string}
  const [gallery, setGallery] = useState([]);
  const [uploading, setUploading] = useState(false);

  // FAQ state
  const [generatedFaq, setGeneratedFaq] = useState([]);

  // New identity/brand state
  const [companyName, setCompanyName] = useState('');
  const [yearsExperience, setYearsExperience] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [coverPhotoUrl, setCoverPhotoUrl] = useState('');
  const [brandsList, setBrandsList] = useState([]);
  const [brandInput, setBrandInput] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  // Anthropic hook
  const { response: aiResponse, isLoading: aiLoading, error: aiError, sendMessage } = useChat(
    'ANTHROPIC',
    'claude-sonnet-4-5-20250929',
    false
  );

  // Track when AI response arrives
  const [awaitingAiResponse, setAwaitingAiResponse] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      setDataLoading(true);
      try {
        // Fetch user data
        const { data: userData } = await supabase
          ?.from('users')
          ?.select('profession, full_name')
          ?.eq('id', user?.id)
          ?.single();

        if (userData) {
          setFullName(userData?.full_name || '');
          setProfessionText(userData?.profession || '');
        }

        // Fetch work areas
        const { data: workAreasData } = await supabase
          ?.from('professional_work_areas')
          ?.select('name')
          ?.eq('user_id', user?.id);

        if (workAreasData) {
          setWorkAreas(workAreasData?.map(w => w?.name)?.filter(Boolean));
        }

        // Fetch professions via join
        const { data: professionsData } = await supabase
          ?.from('professional_professions')
          ?.select('professions(name)')
          ?.eq('user_id', user?.id);

        if (professionsData) {
          const names = professionsData
            ?.map(pp => pp?.professions?.name)
            ?.filter(Boolean);
          setSelectedServices(names);
          // Override professionText with first profession if available
          if (names?.length > 0 && !userData?.profession) {
            setProfessionText(names?.[0]);
          }
        }

        // Fetch existing public profile
        const { data: profileData } = await supabase
          ?.from('professional_public_profiles')
          ?.select('*, view_count, lead_count')
          ?.eq('user_id', user?.id)
          ?.limit(1)
          ?.maybeSingle();

        if (profileData) {
          setExistingSlug(profileData?.slug || '');
          setIsPublished(profileData?.is_published || false);
          setRawInput(profileData?.raw_input || '');
          setGeneratedBio(profileData?.bio_generated || '');
          setGeneratedTagline(profileData?.tagline || '');
          setGeneratedSeoTitle(profileData?.seo_title || '');
          setGeneratedSeoDescription(profileData?.seo_description || '');
          if (profileData?.services_list?.length > 0) {
            setSelectedServices(profileData?.services_list);
          }
          if (profileData?.zones_list?.length > 0) {
            setWorkAreas(profileData?.zones_list);
          }
          setGeneratedFaq(profileData?.faq_list || []);

          // New fields
          setCompanyName(profileData?.company_name || '');
          setYearsExperience(profileData?.years_experience?.toString() || '');
          setLogoUrl(profileData?.logo_url || '');
          setCoverPhotoUrl(profileData?.cover_photo_url || '');
          setBrandsList(profileData?.brands_list || []);
          // gallery_urls now stores objects {url, caption}
          setGallery((profileData?.gallery_urls || [])?.map(item =>
            typeof item === 'string' ? { url: item, caption: '' } : item
          ));
          // Store existing manual_edit_count for increment logic
          setExistingManualEditCount(profileData?.manual_edit_count ?? 0);
          setIsExistingProfile(true);

          // Stats
          setViewCount(profileData?.view_count ?? 0);
          setLeadCount(profileData?.lead_count ?? 0);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Handle AI response when it arrives
  useEffect(() => {
    if (!awaitingAiResponse) return;
    if (aiLoading) return;
    if (aiError) {
      console.error('[PaginaPubblica] AI error received:', aiError);
      setGenerateError('Generazione fallita. Riprova.');
      setIsGenerating(false);
      setAwaitingAiResponse(false);
      return;
    }
    // Use aiResponse != null (not falsy check) so empty string '' still triggers the branch
    // aiResponse is set to '' initially — only proceed once loading is done AND response is non-null
    if (!aiLoading && aiResponse != null && awaitingAiResponse) {
      console.log('[PaginaPubblica] Raw aiResponse received (first 300 chars):', String(aiResponse)?.slice(0, 300));

      if (!aiResponse || aiResponse?.trim() === '') {
        // Response is empty — treat as error
        console.error('[PaginaPubblica] Empty aiResponse after loading completed');
        setGenerateError('Generazione fallita. Riprova.');
        setIsGenerating(false);
        setAwaitingAiResponse(false);
        return;
      }

      try {
        // Step 1: Strip markdown code fences
        let cleaned = aiResponse?.replace(/```json\s*/gi, '')?.replace(/```\s*/g, '')?.trim();

        // Step 2: Extract JSON object — find first '{' to last '}'
        // This handles preamble text or trailing content that may appear in production
        const start = cleaned?.indexOf('{');
        const end = cleaned?.lastIndexOf('}');
        if (start !== -1 && end !== -1 && end > start) {
          cleaned = cleaned?.slice(start, end + 1);
        }

        // Step 3: Sanitize AI quirks — replace Python-style literals with valid JSON equivalents
        // The AI sometimes emits bare `None`, `True`, `False` inside strings or as values
        cleaned = cleaned
          ?.replace(/:\s*None\b/g, ': null')           // value None → null
          ?.replace(/:\s*True\b/g, ': true')            // value True → true
          ?.replace(/:\s*False\b/g, ': false')          // value False → false
          ?.replace(/\bNone\b(?=[,\]\}])/g, 'null')    // None before , ] }
          ?.replace(/\bNone\b\s*\n/g, 'null\n');       // None at end of line inside string

        // Step 4: If None appears inside a string value (e.g. "...ti aiuto\nNone\n...")
        // strip it from within string content — it's a spurious Python null token
        cleaned = cleaned?.replace(/"([^"]*)"/g, (match, inner) => {
          const sanitized = inner
            ?.replace(/\bNone\b/g, '')
            ?.replace(/\n{3,}/g, '\n\n')  // collapse excessive newlines
            ?.trim();
          return `"${sanitized}"`;
        });

        console.log('[PaginaPubblica] Cleaned JSON to parse (first 300 chars):', cleaned?.slice(0, 300));

        const parsed = JSON.parse(cleaned);
        setGeneratedBio(parsed?.bio || '');
        setGeneratedTagline(parsed?.tagline || '');
        setGeneratedSeoTitle(parsed?.seo_title || '');
        setGeneratedSeoDescription(parsed?.seo_description || '');
        if (parsed?.faq) setGeneratedFaq(parsed?.faq);
        setGenerateError('');
        console.log('[PaginaPubblica] AI generation successful');
      } catch (e) {
        console.error('[PaginaPubblica] JSON parse failed:', e?.message, '| Raw:', String(aiResponse)?.slice(0, 500));
        setGenerateError('Generazione fallita. Riprova.');
      }
      setIsGenerating(false);
      setAwaitingAiResponse(false);
    }
  }, [aiResponse, aiLoading, aiError, awaitingAiResponse]);

  const handleGenerate = async () => {
    if (rawInput?.length < 20 || isGenerating) return;
    setIsGenerating(true);
    setGenerateError('');
    try {
      const res = await fetch(
        'https://vltfzozjztfaeifjqpcg.supabase.co/functions/v1/generate-professional-bio',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env?.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            nome: companyName || fullName || '',
            anni_esperienza: yearsExperience || '',
            professione: professionText,
            marchi: brandsList,
            testo_libero: rawInput
          })
        }
      );
      if (!res?.ok) {
        const errText = await res?.text();
        console.error('[handleGenerate] error:', res?.status, errText);
        throw new Error('HTTP ' + res.status);
      }
      const data = await res?.json();
      setGeneratedBio(data?.bio || '');
      setGeneratedTagline(data?.tagline || '');
      setGeneratedSeoTitle(data?.seo_title || '');
      setGeneratedSeoDescription(data?.seo_description || '');
      if (data?.faq) setGeneratedFaq(data?.faq);
      setGenerateError('');
    } catch (err) {
      console.error('[handleGenerate] fetch error:', err);
      setGenerateError('Generazione fallita. Riprova.');
    } finally {
      setIsGenerating(false);
    }
  };

  const generateSlug = () => {
    const namePart = fullName?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '') || '';
    const profPart = professionText?.toLowerCase()?.replace(/\s+/g, '-')?.replace(/[^a-z0-9-]/g, '') || '';
    if (namePart && profPart) {
      return `${namePart}-${profPart}`;
    }
    const random = Math.floor(1000 + Math.random() * 9000);
    return `${profPart || 'professionista'}-${random}`;
  };

  const handlePublish = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess('');

    try {
      const slug = existingSlug || generateSlug();

      const upsertData = {
        user_id: user?.id,
        slug,
        display_name: companyName || fullName || professionText,
        tagline: generatedTagline,
        bio_generated: generatedBio,
        raw_input: rawInput,
        seo_title: generatedSeoTitle,
        seo_description: generatedSeoDescription,
        is_published: true,
        generated_at: new Date()?.toISOString(),
        services_list: selectedServices,
        zones_list: workAreas,
        gallery_urls: gallery,
        profession_text: professionText,
        faq_list: generatedFaq || [],
        company_name: companyName,
        years_experience: yearsExperience ? parseInt(yearsExperience) : null,
        logo_url: logoUrl,
        cover_photo_url: coverPhotoUrl,
        brands_list: brandsList,
      };

      const { error: upsertError } = await supabase
        ?.from('professional_public_profiles')
        ?.upsert(upsertData, { onConflict: 'user_id' });

      if (upsertError) throw upsertError;

      // Increment manual_edit_count only on explicit user-triggered saves that are updates (not first creation)
      if (isExistingProfile) {
        const newCount = existingManualEditCount + 1;
        await supabase
          ?.from('professional_public_profiles')
          ?.update({ manual_edit_count: newCount })
          ?.eq('user_id', user?.id);
        setExistingManualEditCount(newCount);
      }

      setExistingSlug(slug);
      setIsPublished(true);
      setIsExistingProfile(true);
      setSaveSuccess(slug);
    } catch (err) {
      setSaveError('Errore durante la pubblicazione. Riprova.');
      console.error('Publish error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !user?.id) return;
    setUploadingLogo(true);
    try {
      const path = `${user?.id}/logo-${Date.now()}-${file?.name}`;
      const { error: uploadError } = await supabase?.storage?.from('professional-portfolio')?.upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase?.storage?.from('professional-portfolio')?.getPublicUrl(path);
      setLogoUrl(publicUrl);
    } catch (err) {
      console.error('Logo upload error:', err);
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !user?.id) return;
    setUploadingCover(true);
    try {
      const path = `${user?.id}/cover-${Date.now()}-${file?.name}`;
      const { error: uploadError } = await supabase?.storage?.from('professional-portfolio')?.upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase?.storage?.from('professional-portfolio')?.getPublicUrl(path);
      setCoverPhotoUrl(publicUrl);
    } catch (err) {
      console.error('Cover upload error:', err);
    } finally {
      setUploadingCover(false);
      e.target.value = '';
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e?.target?.files || []);
    if (!files?.length || !user?.id) return;
    const slots = 15 - gallery?.length;
    const toUpload = files?.slice(0, slots);
    for (const file of toUpload) {
      try {
        const path = `${user?.id}/${Date.now()}-${file?.name}`;
        const { data, error: uploadError } = await supabase?.storage?.from('professional-portfolio')?.upload(path, file, { upsert: true });
        if (uploadError) { console.error('Gallery upload error:', uploadError); continue; }
        const { data: { publicUrl } } = supabase?.storage?.from('professional-portfolio')?.getPublicUrl(data?.path);
        setGallery(prev => prev?.length < 15 ? [...prev, { url: publicUrl, caption: '' }] : prev);
      } catch (err) {
        console.error('Gallery upload error:', err);
      }
    }
    e.target.value = '';
  };

  const headingStyle = {
    fontSize: '15px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '14px',
    marginTop: '0',
  };

  if (dataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <ProfessionalDashboardNavbar />
      <div className="max-w-[720px] mx-auto px-4 py-10">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">La tua pagina pubblica</h1>
          <p className="text-gray-500 text-sm mt-1">Crea la tua pagina professionale e fatti trovare su Google — gratis.</p>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">

          {/* SECTION A — Identità */}
          <div style={{ marginTop: 0 }}>
            <h3 style={headingStyle}>1. Chi sei</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                  Nome azienda o tuo nome
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={e => setCompanyName(e?.target?.value)}
                  placeholder="Es: ZV Impianti Elettrici"
                  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#111827', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                  Anni di esperienza
                </label>
                <input
                  type="number"
                  value={yearsExperience}
                  onChange={e => setYearsExperience(e?.target?.value)}
                  placeholder="Es: 15"
                  min="0"
                  max="60"
                  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', color: '#111827', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
            </div>
          </div>

          {/* SECTION B — Logo e foto copertina */}
          <div style={{ marginTop: '20px' }}>
            <h3 style={headingStyle}>2. Logo e foto copertina</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>

              {/* Logo tile */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                  Logo aziendale
                </label>
                <div
                  style={{ position: 'relative', height: '120px', border: '2px dashed #e5e7eb', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backgroundColor: '#f9fafb', overflow: 'hidden' }}
                  onClick={() => !uploadingLogo && document.getElementById('logo-upload')?.click()}
                >
                  {logoUrl ? (
                    <>
                      <img src={logoUrl} alt="Logo" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} />
                      <button
                        type="button"
                        onClick={e => { e?.stopPropagation(); setLogoUrl(''); }}
                        style={{ position: 'absolute', top: '6px', right: '6px', width: '22px', height: '22px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                      >×</button>
                    </>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Camera size={24} color="#9ca3af" />
                      <span style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                        {uploadingLogo ? 'Caricamento...' : 'Carica logo'}
                      </span>
                    </div>
                  )}
                </div>
                <input id="logo-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
              </div>

              {/* Cover tile */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                  Foto di copertina
                </label>
                <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>Foto del tuo lavoro più bello</span>
                <div
                  style={{ position: 'relative', height: '120px', border: '2px dashed #e5e7eb', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backgroundColor: '#f9fafb', overflow: 'hidden' }}
                  onClick={() => !uploadingCover && document.getElementById('cover-upload')?.click()}
                >
                  {coverPhotoUrl ? (
                    <>
                      <img src={coverPhotoUrl} alt="Copertina" style={{ width: '100%', height: '100px', objectFit: 'cover' }} />
                      <button
                        type="button"
                        onClick={e => { e?.stopPropagation(); setCoverPhotoUrl(''); }}
                        style={{ position: 'absolute', top: '6px', right: '6px', width: '22px', height: '22px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                      >×</button>
                    </>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Camera size={24} color="#9ca3af" />
                      <span style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                        {uploadingCover ? 'Caricamento...' : 'Carica copertina'}
                      </span>
                    </div>
                  )}
                </div>
                <input id="cover-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleCoverUpload} />
              </div>
            </div>
          </div>

          {/* SECTION C — Gallery con didascalie */}
          <div style={{ marginTop: '20px' }}>
            <h3 style={headingStyle}>3. Foto dei tuoi lavori</h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              Aggiungi fino a 15 foto. Puoi aggiungere una breve descrizione sotto ogni immagine.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              {gallery?.map((item, index) => (
                <div key={index} style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                  <img
                    src={item?.url}
                    alt={`Foto lavoro ${index + 1}`}
                    style={{ width: '100%', height: '100px', objectFit: 'cover', display: 'block' }}
                  />
                  <button
                    type="button"
                    onClick={() => setGallery(prev => prev?.filter((_, i) => i !== index))}
                    style={{ position: 'absolute', top: '5px', right: '5px', width: '20px', height: '20px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                  >×</button>
                  <input
                    type="text"
                    value={item?.caption}
                    onChange={e => setGallery(prev => prev?.map((it, i) => i === index ? { ...it, caption: e?.target?.value } : it))}
                    placeholder="Descrizione breve..."
                    style={{ width: '100%', border: 'none', borderTop: '1px solid #e5e7eb', padding: '5px 8px', fontSize: '11px', color: '#374151', outline: 'none', boxSizing: 'border-box', backgroundColor: '#fff' }}
                  />
                </div>
              ))}
              {gallery?.length < 15 && (
                <div
                  onClick={() => document.getElementById('gallery-upload')?.click()}
                  style={{ height: '130px', border: '2px dashed #e5e7eb', borderRadius: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backgroundColor: '#f9fafb' }}
                >
                  <span style={{ fontSize: '24px', color: '#9ca3af', lineHeight: 1 }}>+</span>
                  <span style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Aggiungi</span>
                </div>
              )}
            </div>
            <input
              id="gallery-upload"
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleGalleryUpload}
            />
          </div>

          {/* SECTION D — Marchi e certificazioni */}
          <div style={{ marginTop: '20px' }}>
            <h3 style={headingStyle}>4. Marchi e certificazioni</h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
              Es: BTicino, Legrand, ABB, VIMAR — aumentano la fiducia del cliente
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
              {brandsList?.map((brand, i) => (
                <span key={i} style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', borderRadius: '20px', padding: '4px 12px', fontSize: '13px', display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                  {brand}
                  <button
                    type="button"
                    onClick={() => setBrandsList(prev => prev?.filter((_, idx) => idx !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: '14px', lineHeight: 1, padding: 0 }}
                  >×</button>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={brandInput}
                onChange={e => setBrandInput(e?.target?.value)}
                onKeyDown={e => {
                  if (e?.key === 'Enter') {
                    e?.preventDefault();
                    if (brandInput?.trim() && brandsList?.length < 15) {
                      setBrandsList(prev => [...prev, brandInput?.trim()]);
                      setBrandInput('');
                    }
                  }
                }}
                placeholder="Es: BTicino"
                style={{ flex: 1, padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
              />
              <button
                type="button"
                onClick={() => {
                  if (brandInput?.trim() && brandsList?.length < 15) {
                    setBrandsList(prev => [...prev, brandInput?.trim()]);
                    setBrandInput('');
                  }
                }}
                style={{ backgroundColor: '#104d98', color: 'white', padding: '6px 14px', borderRadius: '8px', fontSize: '13px', border: 'none', cursor: 'pointer' }}
              >
                Aggiungi
              </button>
            </div>
          </div>

          {/* SECTION E — Descrizione */}
          <div style={{ marginTop: '20px', marginBottom: '0' }}>
            <h3 style={headingStyle}>5. Raccontaci di te ✍️</h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '10px', lineHeight: '1.6' }}>
              Scrivi di te liberamente — anche con errori. L'AI usa anche i campi sopra (nome, anni, marchi) per costruire la pagina.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
              {['💼 Da quanti anni lavori?', '🏆 Hai certificazioni?', '📍 Dove operi?', '⚡ Cosa ti distingue?']?.map(hint => (
                <button
                  key={hint}
                  type="button"
                  onClick={() => setRawInput(prev => prev + (prev ? '\n' : '') + hint?.split(' ')?.slice(1)?.join(' ') + ' ')}
                  style={{ fontSize: '12px', color: '#374151', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '20px', padding: '4px 12px', cursor: 'pointer', fontWeight: '500' }}
                >
                  {hint}
                </button>
              ))}
            </div>
            <textarea
              rows={6}
              value={rawInput}
              onChange={e => setRawInput(e?.target?.value)}
              placeholder={"Esempio: Sono Mario Rossi, elettricista con 15 anni di esperienza. Mi occupo di impianti civili e industriali, domotica, fotovoltaico e interventi di emergenza. Lavoro con puntualità e rilascio sempre documentazione conforme alla normativa CEI. Zona Novara e provincia."}
              style={{ width: '100%', border: '1.5px solid #d1d5db', borderRadius: '10px', padding: '12px', fontSize: '14px', color: '#111827', resize: 'vertical', outline: 'none', transition: 'border-color 0.15s', boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = '#104d98'}
              onBlur={e => e.target.style.borderColor = '#d1d5db'}
            />
            <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
              Minimo 20 caratteri · L'AI si occupa del resto: grammatica, stile, SEO e FAQ incluse automaticamente
            </p>
          </div>

          {/* SECTION F — Genera con AI */}
          <div className="mb-6" style={{ marginTop: '20px' }}>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={rawInput?.length < 20 || isGenerating}
              className="w-full py-3 rounded-lg font-semibold text-white text-sm transition-opacity disabled:opacity-50"
              style={{ backgroundColor: '#32b3e5' }}
            >
              {isGenerating ? 'Generazione in corso...' : 'Genera con AI ✨'}
            </button>
            {generateError && (
              <p className="text-sm text-red-600 mt-2">{generateError}</p>
            )}
          </div>

          {/* Preview */}
          {generatedBio && (
            <div className="mb-6">
              <div className="bg-blue-50 rounded-xl p-6 mb-4">
                <p className="italic text-lg text-gray-800 mb-3">"{generatedTagline}"</p>
                <p className="text-sm text-gray-600 mb-3">{generatedBio}</p>
                <p className="text-xs text-gray-400">{generatedSeoTitle}</p>
              </div>

              {!saveSuccess ? (
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={isSaving}
                  className="px-6 py-3 rounded-lg font-semibold text-white text-sm transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: '#104d98' }}
                >
                  {isSaving ? 'Pubblicazione...' : 'Pubblica →'}
                </button>
              ) : (
                <div className="flex items-center gap-4 flex-wrap">
                  <p className="text-sm text-green-700 font-medium">
                    ✓ Pagina pubblicata su <span className="font-mono">volaptic.com/p/{saveSuccess}</span>
                  </p>
                  <button
                    type="button"
                    onClick={() => window.open(`/p/${saveSuccess}`, '_blank')}
                    className="text-sm font-semibold text-white rounded-lg px-4 py-2"
                    style={{ backgroundColor: '#104d98' }}
                  >
                    Visualizza →
                  </button>
                </div>
              )}

              {saveError && (
                <p className="text-sm text-red-600 mt-2">{saveError}</p>
              )}
            </div>
          )}

          {/* Already published notice */}
          {isPublished && existingSlug && !saveSuccess && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-700 font-medium">
                ✓ Pagina attiva su <span className="font-mono">volaptic.com/p/{existingSlug}</span>
              </p>
              <button
                type="button"
                onClick={() => window.open(`/p/${existingSlug}`, '_blank')}
                className="mt-2 text-sm font-semibold text-white rounded-lg px-4 py-2"
                style={{ backgroundColor: '#104d98' }}
              >
                Visualizza →
              </button>
            </div>
          )}
        </div>

        {/* ANALYTICS & SHARE TOOLS SECTION */}
        {isPublished && existingSlug && (
          <div className="mt-8 space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Statistiche pagina</h2>
              <p className="text-sm text-gray-500 mb-4">Monitora le visite alla tua pagina pubblica.</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">{viewCount}</p>
                  <p className="text-xs text-gray-500 mt-1">Visite totali</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{leadCount}</p>
                  <p className="text-xs text-gray-500 mt-1">Richieste ricevute</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Condividi la tua pagina</h2>
              <p className="text-sm text-gray-500 mb-4">Condividi il link con i tuoi clienti o sui social.</p>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-700 truncate">
                  volaptic.com/p/{existingSlug}
                </div>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard?.writeText(`https://volaptic.com/p/${existingSlug}`); }}
                  className="px-3 py-2 text-sm font-semibold text-white rounded-lg whitespace-nowrap"
                  style={{ backgroundColor: '#104d98' }}
                >
                  Copia link
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Visita la mia pagina professionale su Volaptic: https://volaptic.com/p/${existingSlug}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                  style={{ backgroundColor: '#25D366' }}
                >
                  WhatsApp
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://volaptic.com/p/${existingSlug}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                  style={{ backgroundColor: '#1877F2' }}
                >
                  Facebook
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://volaptic.com/p/${existingSlug}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                  style={{ backgroundColor: '#0A66C2' }}
                >
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ProfessionalPaginaPubblica;
